import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUser, faDna, faVial, faFlask, faBacteria, 
    faTooth, faDroplet, faFlaskVial, faOilCan, 
    faAtom, faCircleNodes, faCopy, faCheck 
} from '@fortawesome/free-solid-svg-icons';
import './AppUserDetails.css';
import OmicsViewModal from '../../../SampleManager/OmicsView/OmicsView';

function AppUserDetails({ userDetails }) {
    const [showOmicsModal, setShowOmicsModal] = useState(false);
    const [selectedOmicsType, setSelectedOmicsType] = useState(null);
    const [selectedOmicsId, setSelectedOmicsId] = useState(null);
    const [omicsModalData, setOmicsModalData] = useState({});
    
    if (!userDetails) return null;
    
    const user = userDetails.userDetails || {};
    
    // Extract available reports from patientMetadata in userDetails
    const availableReports = user?.patientMetadata?.[0]?.available_reports || {};
    
    // Convert reports object to array for rendering
    const reportsArray = Object.entries(availableReports).map(([category, values]) => ({
        category,
        values: Array.isArray(values) ? values : [],
        icon: getReportIcon(category)
    })).filter(report => report.values.length > 0);
    
    // Handle report click - open OmicsViewModal exactly like in ProjectDetails.js
    const handleReportClick = (reportType, reportId) => {
        // Map the report type to the expected OmicsView type
        let omicsType;
        switch(reportType) {
            case 'WGS':
                omicsType = 'Whole Genome';
                break;
            case 'Gut Health':
                omicsType = 'Gut Health';
                break;
            case 'Oral Health':
                omicsType = 'Oral Health';
                break;
            case 'Transcriptome':
                omicsType = 'Transcriptome';
                break;
            default:
                omicsType = reportType;
        }

        setSelectedOmicsType(omicsType);
        setSelectedOmicsId(reportId); // Use the report ID directly
        setOmicsModalData({
            patientId: user.PatientID || '',
            ...user
        });
        setShowOmicsModal(true);
    };
    
    // Use exact same getReportIcon function as in ProjectDetails.js
    function getReportIcon(reportType) {
        switch(reportType) {
            case 'Genomics':
            case 'WGS': 
                return faDna;
            case 'Gut Health': 
                return faBacteria;
            case 'Oral Health': 
                return faTooth;
            case 'Metabolome Plasma': 
                return faDroplet;
            case 'Metabolome Urine': 
                return faFlaskVial;
            case 'Metabolome Lipids': 
                return faOilCan;
            case 'Proteomics': 
                return faAtom;
            case 'Transcriptome': 
                return faCircleNodes;
            default: 
                return faDna;
        }
    }
    
    // Get color for report category
    function getReportColor(category) {
        const lowerCategory = category.toLowerCase();
        if (lowerCategory.includes('gut')) {
            return '#e67e22';
        } else if (lowerCategory.includes('oral')) {
            return '#9b59b6';
        } else if (lowerCategory.includes('metabolome')) {
            return '#3498db';
        } else if (lowerCategory.includes('wgs') || lowerCategory.includes('genom')) {
            return '#2ecc71';
        } else if (lowerCategory.includes('proteom')) {
            return '#f1c40f';
        } else if (lowerCategory.includes('transcript')) {
            return '#e74c3c';
        } else {
            return '#1abc9c';
        }
    }
    
    return (
        <div className="AppUserDetails-container">
            
            {/* OmicsView Modal */}
            {showOmicsModal && (
                <OmicsViewModal
    isOpen={showOmicsModal}
    onClose={() => setShowOmicsModal(false)}
    omicsType={selectedOmicsType}
    omicsId={selectedOmicsId}
    omicsData={omicsModalData}
/>

            )}
            <div className="AppUserDetails-profile">
                <div className="AppUserDetails-avatar">
                    <div className="AppUserDetails-avatar-inner">
                        <FontAwesomeIcon icon={faUser} />
                        <div className="AppUserDetails-avatar-initials">
                            {user?.First_Name?.charAt(0) || ''}{user?.Last_Name?.charAt(0) || ''}
                        </div>
                    </div>
                </div>
                <div className="AppUserDetails-info">
                <div className="AppUserDetails-infotext">
  {`${user?.First_Name ? user.First_Name[0].toUpperCase() + user.First_Name.slice(1).toLowerCase() : ''} ${user?.Last_Name ? user.Last_Name[0].toUpperCase() + user.Last_Name.slice(1).toLowerCase() : ''}`}
<div className="AppUserDetails-invitation-link-container">
  <input 
    type="text" 
    className="AppUserDetails-invitation-link-input" 
    value={`https://account-activation.onelink.me/tKHW/?deep_link_sub1=account-activate&deep_link_sub2=${user?.identifier || ''}`} 
    readOnly 
  />
  <button 
    className="AppUserDetails-invitation-copy-btn" 
    onClick={() => {
      navigator.clipboard.writeText(`https://account-activation.onelink.me/tKHW/?deep_link_sub1=account-activate&deep_link_sub2=${user?.identifier || ''}`);
      const copyBtn = document.querySelector('.AppUserDetails-invitation-copy-btn');
      copyBtn.classList.add('AppUserDetails-invitation-copy-success');
      setTimeout(() => {
        copyBtn.classList.remove('AppUserDetails-invitation-copy-success');
      }, 1500);
    }}
    title="Copy invitation link"
  >
    <FontAwesomeIcon icon={faCopy} className="AppUserDetails-invitation-copy-icon" />
    <FontAwesomeIcon icon={faCheck} className="AppUserDetails-invitation-success-icon" />
  </button>
</div>
</div>

                   <div className="appuserinfo">
                   <div className="appuserleft">
                    <div className="AppUserDetails-demographics">
                        <div className="AppUserDetails-demographic-item">
                            <span className="AppUserDetails-label">Age:</span>
                            <span className="AppUserDetails-value">{user?.Age || 'N/A'}</span>
                        </div>
                        <div className="AppUserDetails-demographic-item">
                            <span className="AppUserDetails-label">Sex:</span>
                            <span className="AppUserDetails-value">{user?.Sex || 'N/A'}</span>
                        </div>
                        <div className="AppUserDetails-demographic-item">
                            <span className="AppUserDetails-label">Height:</span>
                            <span className="AppUserDetails-value">{user?.Height ? `${user.Height} cm` : 'N/A'}</span>
                        </div>
                        <div className="AppUserDetails-demographic-item">
                            <span className="AppUserDetails-label">Weight:</span>
                            <span className="AppUserDetails-value">{user?.Weight ? `${user.Weight} kg` : 'N/A'}</span>
                        </div>
                        <div className="AppUserDetails-demographic-item">
                            <span className="AppUserDetails-label">Location:</span>
                            <span className="AppUserDetails-value">{user?.Country || 'N/A'}</span>
                        </div>
                        <div className="AppUserDetails-demographic-item">
                            <span className="AppUserDetails-label">Email:</span>
                            <span className="AppUserDetails-value">{user?.Email || 'N/A'}</span>
                        </div>
                    </div>
                    </div>
                        {reportsArray.length > 0 && (
                            <div className="appuserright">
                                <div className="AppUserDetails-omics">
                                    <div className="AppUserDetails-report-grid">
                                        {reportsArray.map((report, index) => (
                                            report.values.map((reportId, idx) => (
                                                <div 
                                                    key={`${index}-${idx}`} 
                                                    className="PMP-report-slot"
                                                    title={report.category}
                                                    onClick={() => handleReportClick(report.category, reportId)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={getReportIcon(report.category)}
                                                        className="PMP-report-icon"
                                                    />
                                                    <span className="PMP-report-label">
                                                        {report.category ? (
                                                            report.category.startsWith('Metabolome') ?
                                                                report.category.split(' ')[1] :
                                                                report.category.split(' ')[0]
                                                        ) : ''}
                                                    </span>
                                                </div>
                                            ))
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                   </div>
          
            </div>
            </div>
        </div>
    );
}

export default AppUserDetails;
